import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CalculationHistoryPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await api.get("/calculation-logs");
      setLogs(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-6" data-testid="calculation-history-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Calculation History
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          View all past dhoti cost calculations.
        </p>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Date</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Size</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Border</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Warp</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Weft</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Total Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-stone-400">
                  No calculations yet. Use the calculator to create your first costing.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, idx) => (
                <TableRow
                  key={log.id}
                  className="animate-row hover:bg-stone-50/50 cursor-pointer"
                  style={{ animationDelay: `${idx * 30}ms` }}
                  data-testid={`history-row-${log.id}`}
                  onClick={() => setSelected(log)}
                >
                  <TableCell className="px-4 py-2.5 text-sm text-stone-600">{formatDate(log.created_at)}</TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-stone-900 font-medium">{log.size}</TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-stone-700">{log.border_type}</TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-stone-700">{log.warp_count} / {log.warp_quality}</TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-stone-700">{log.weft_count} / {log.weft_quality}</TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right font-mono font-semibold text-[#C2410C]">{(log.final_cost_per_dhoti || log.total_cost)?.toFixed(2)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-stone-400">{logs.length} calculation(s)</p>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Calculation Detail</DialogTitle>
            <DialogDescription>
              {selected?.size} - {selected?.border_type} | {formatDate(selected?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Specs</p>
                <div className="grid grid-cols-2 gap-1.5 text-sm">
                  <span className="text-stone-500">Fabric Width</span>
                  <span className="text-right font-mono">{selected.fabric_width}</span>
                  <span className="text-stone-500">Border Width</span>
                  <span className="text-right font-mono">{selected.border_width}</span>
                  <span className="text-stone-500">Reed</span>
                  <span className="text-right font-mono">{selected.reed}</span>
                  <span className="text-stone-500">Pick</span>
                  <span className="text-right font-mono">{selected.pick}</span>
                  <span className="text-stone-500">Reed Space</span>
                  <span className="text-right font-mono">{selected.reed_space}</span>
                  <span className="text-stone-500">Warp Ends</span>
                  <span className="text-right font-mono">{selected.warp_ends}</span>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Costs</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Warp ({selected.warp_count}/{selected.warp_quality})</span>
                    <span className="font-mono">{selected.warp_total_cost?.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-stone-400 pl-4">
                    <span>Raw: {selected.warp_raw_cost?.toFixed(4)} + Sizing: {selected.warp_sizing_charge?.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Weft ({selected.weft_count}/{selected.weft_quality})</span>
                    <span className="font-mono">{selected.weft_cost?.toFixed(4)}</span>
                  </div>
                  {selected.has_bobbin1 && (
                    <div className="flex justify-between">
                      <span className="text-stone-500">Bobbin - Polyester</span>
                      <span className="font-mono">{selected.bobbin1_cost?.toFixed(4)}</span>
                    </div>
                  )}
                  {selected.has_bobbin2 && (
                    <div className="flex justify-between">
                      <span className="text-stone-500">Bobbin - Jari</span>
                      <span className="font-mono">{selected.bobbin2_cost?.toFixed(4)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium">
                    <span className="text-stone-700">Total Bobbin ({selected.bobbin_ends} ends)</span>
                    <span className="font-mono">{selected.total_bobbin_cost?.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Kuri ({selected.kuri_count}/{selected.kuri_quality})</span>
                    <span className="font-mono">{selected.kuri_cost?.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Weaving Wages ({selected.pick_rate} x {selected.pick})</span>
                    <span className="font-mono">{selected.weaving_wages?.toFixed(4)}</span>
                  </div>
                </div>
              </div>
              <Separator />
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between font-medium">
                  <span className="text-stone-700">Cost per Mtr</span>
                  <span className="font-mono">{selected.cost_per_mtr?.toFixed(4)}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-stone-700">Cost per Dhoti ({selected.size}m)</span>
                  <span className="font-mono">{selected.cost_per_dhoti?.toFixed(4)}</span>
                </div>
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Weaver Profit ({selected.weaver_profit_pct}%)</span>
                <span className="font-mono">{selected.profit_amount?.toFixed(4)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm font-medium">
                <span className="text-stone-700">Greige Cost per Dhoti</span>
                <span className="font-mono">{selected.greige_cost_per_dhoti?.toFixed(2)}</span>
              </div>
              {(selected.processing_cost > 0 || selected.folding_cost > 0) && (
                <>
                  <Separator />
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-500">Processing ({selected.processing_type_name})</span>
                      <span className="font-mono">{selected.processing_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Folding</span>
                      <span className="font-mono">{selected.folding_cost?.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
              <Separator />
              <div className="flex justify-between text-sm font-medium">
                <span className="text-stone-700">Final Cost per Dhoti</span>
                <span className="font-mono">{selected.final_cost_per_dhoti?.toFixed(2)}</span>
              </div>
              {selected.warehouse_cost > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-500">Warehouse ({selected.warehouse_pct}%)</span>
                    <span className="font-mono">{selected.warehouse_cost?.toFixed(2)}</span>
                  </div>
                </>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Landed Cost per Dhoti</span>
                <span className="font-mono text-[#C2410C]">{selected.landed_cost?.toFixed(2)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
